import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  reportsApi,
  type EvidencePackResponse,
  type CraReadinessJsonResponse,
  type IncidentsRollupResponse,
  type IncidentsRulePolicyDoc,
  type IncidentsRulePolicyPreviewResponse,
  type ReliabilityBundlePreviewResponse,
  type ReliabilityProofPackPreviewResponse,
  type SiteReadinessJsonResponse,
} from '../../services/reportsApi';
import {
  REPORT_KIND_LABELS,
  REPORT_TASK_TO_RECOMMENDED_KIND,
  getReportDialogFieldVisibility,
  inferReportTask,
  isAdvancedOnlyArtifactTask,
  resolveArtifactFlowPreset,
  type ReportDialogKind,
  type ReportDialogTask,
} from '../../utils/reportFlow';
import { readAcceptanceProofCache, writeAcceptanceProofCache } from '../../utils/acceptanceArtifacts';
import { resolveReportWindowIso, toIsoFromLocalInput, toLocalInputValue } from './useIncidentsTimeRange';
import {
  SAFETY_REPORT_BUILD_INCOMPLETE_HELPER,
  SAFETY_REPORT_BUILD_TIMEOUT_HELPER,
} from '../acceptanceProof/safetyReportMvpPresentation';
import {
  resolveReportProductName,
  type HazardClassKey,
  type SafetyReportTypeMode,
} from '../acceptanceProof/hazardClassReportOptions';
import {
  buildSafetyReportEvidencePackFilters,
  parseSafetyReportTypeFromSearchParams,
  safetyReportScopeCacheSegment,
} from './safetyReportEvidencePackParams';

type ReliabilityBundleScope = 'site' | 'device' | 'camera' | 'zone';

type SetSearchParams = (next: URLSearchParams, options?: { replace?: boolean }) => void;

type UseIncidentsReportsArgs = {
  filterCameraId: string;
  filterZoneId: string;
  filterDeviceId: string;
  timeRange: {
    fromDate?: string;
    toDate?: string;
  };
  searchParams: URLSearchParams;
  setSearchParams: SetSearchParams;
  getScopedSiteId: () => string;
  getScopedOrgId: () => string;
  downloadBlob: (blob: Blob, filename: string) => void;
};

export function resolveInitialReportWindowInput(value: string | null | undefined): string {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return toLocalInputValue(raw) || raw;
}

export function describeTrustPackStartError(
  response?: { error?: string; detail?: string } | null,
  err?: any
): string {
  const errorCode = String(
    response?.error || err?.response?.data?.error || err?.error || ''
  ).trim().toLowerCase();
  const detail = String(
    response?.detail || err?.response?.data?.detail || err?.message || ''
  ).trim();

  if (errorCode === 'deploy_runner_not_configured') {
    return 'Customer Handoff Bundle is not available in this environment yet. Deploy runner is not configured.';
  }
  if (errorCode === 'deploy_runner_unreachable') {
    return 'Customer Handoff Bundle is temporarily unavailable because the handoff builder service cannot be reached.';
  }
  if (errorCode === 'deploy_runner_build_rejected') {
    return detail
      ? `Customer Handoff Bundle could not be started. ${detail}`
      : 'Customer Handoff Bundle could not be started because the handoff builder rejected this request.';
  }
  if (detail) {
    return `Customer Handoff Bundle could not be started. ${detail}`;
  }
  return 'Customer Handoff Bundle could not be started.';
}

export function describeProofPackStartError(
  response?: { error?: string; detail?: string; status_code?: number | string } | null,
  err?: any
): string {
  const errorCode = String(
    response?.error || err?.response?.data?.error || err?.error || ''
  ).trim().toLowerCase();
  const detail = String(
    response?.detail || err?.response?.data?.detail || err?.message || ''
  ).trim();
  const statusCodeRaw =
    response?.status_code
    ?? err?.response?.status
    ?? err?.status
    ?? err?.job?.result_status_code;
  const statusCode = Number(statusCodeRaw);

  if (
    statusCode === 502
    || errorCode === 'bad_gateway'
    || errorCode === 'gateway_error'
    || errorCode === 'db_unreachable'
  ) {
    return detail
      ? `Evidence bundle could not be created right now. ${detail}`
      : 'Evidence bundle could not be created right now. The report service hit a temporary gateway error. Try again.';
  }
  const isTimeout = (
    err?.code === 'ECONNABORTED'
    || statusCode === 504
    || errorCode === 'gateway_timeout'
    || /timeout/i.test(detail)
    || /timeout/i.test(String(err?.message || ''))
  );
  if (isTimeout) {
    return SAFETY_REPORT_BUILD_TIMEOUT_HELPER;
  }
  if (errorCode === 'evidence_pack_incomplete') {
    return detail || SAFETY_REPORT_BUILD_INCOMPLETE_HELPER;
  }
  if (detail) {
    return `Evidence bundle could not be created. ${detail}`;
  }
  return SAFETY_REPORT_BUILD_INCOMPLETE_HELPER;
}

export function shouldRetryProofPackStartError(
  response?: { error?: string; status_code?: number | string } | null,
  err?: any,
): boolean {
  const errorCode = String(
    response?.error || err?.response?.data?.error || err?.error || ''
  ).trim().toLowerCase();
  const statusCodeRaw =
    response?.status_code
    ?? err?.response?.status
    ?? err?.status
    ?? err?.job?.result_status_code;
  const statusCode = Number(statusCodeRaw);

  return (
    statusCode === 502
    || statusCode === 503
    || statusCode === 504
    || errorCode === 'bad_gateway'
    || errorCode === 'gateway_error'
    || errorCode === 'db_unreachable'
    || errorCode === 'service_unavailable'
    || errorCode === 'gateway_timeout'
  );
}

function normalizeEvidencePackPreview(
  response?: EvidencePackResponse | null,
): ReliabilityProofPackPreviewResponse | null {
  if (!response || typeof response !== 'object') return null;
  if (response.ok === false) {
    return {
      ok: false,
      evidence_pack: response.evidence_pack,
      persisted_report: response.persisted_report,
      links: (response as any)?.links,
      report_visuals: Array.isArray(response.report_visuals) ? response.report_visuals : [],
      ...(response.error ? { error: response.error } : {}),
      ...(response.detail ? { detail: response.detail } : {}),
    } as ReliabilityProofPackPreviewResponse;
  }
  const evidencePack = response.evidence_pack;
  const proofPack = evidencePack?.contents?.reliability_proof_pack;
  if (!evidencePack || !proofPack || typeof proofPack !== 'object') {
    return {
      ok: false,
      evidence_pack: evidencePack,
      links: (response as any)?.links,
      report_visuals: Array.isArray(response.report_visuals) ? response.report_visuals : [],
      error: 'evidence_pack_incomplete',
      detail: SAFETY_REPORT_BUILD_INCOMPLETE_HELPER,
    } as ReliabilityProofPackPreviewResponse;
  }
  const normalized = proofPack as ReliabilityProofPackPreviewResponse;
  return {
    ...normalized,
    ok: normalized.ok !== false,
    evidence_pack: evidencePack,
    persisted_report: response.persisted_report || normalized.persisted_report,
    links: (response as any)?.links,
    report_visuals:
      Array.isArray(normalized.report_visuals) && normalized.report_visuals.length > 0
        ? normalized.report_visuals
        : Array.isArray(response.report_visuals)
          ? response.report_visuals
          : [],
  };
}

function firstNonEmpty(...values: unknown[]): string {
  for (const value of values) {
    const text = String(value || '').trim();
    if (text) return text;
  }
  return '';
}

function deriveWorkflowIdForProofPreview(
  preview: ReliabilityProofPackPreviewResponse | null | undefined,
  searchParams: URLSearchParams,
): string {
  return firstNonEmpty(
    preview?.persisted_report?.workflow_id,
    preview?.evidence_pack?.workflow_anchor?.group_key,
    preview?.evidence_pack?.report_projection?.workflow_anchor?.group_key,
    preview?.evidence_pack?.workflow_anchor?.proof_scope_id,
    preview?.evidence_pack?.report_projection?.proof_scope_id,
    preview?.proof_readiness?.summary?.risk_pattern_summary?.group_key,
    searchParams.get('proof_scope_id'),
    searchParams.get('case_id'),
    searchParams.get('incident_id'),
  );
}

function deriveRepresentativeIncidentIdForProofPreview(
  preview: ReliabilityProofPackPreviewResponse | null | undefined,
  searchParams: URLSearchParams,
): string {
  return firstNonEmpty(
    preview?.evidence_pack?.workflow_anchor?.review_anchor_incident_id,
    preview?.evidence_pack?.report_projection?.anchor_incident_id,
    preview?.evidence_pack?.report_projection?.technical_trace?.anchor_incident_id,
    preview?.evidence_pack?.report_projection?.pattern_trace?.representative_incident_id,
    preview?.proof_readiness?.summary?.risk_pattern_summary?.representative_incident_id,
    preview?.case_file?.incidents?.[0]?.incident_id,
    searchParams.get('case_id'),
    searchParams.get('incident_id'),
  );
}

async function attachPersistedSafetyReportDraft(args: {
  preview: ReliabilityProofPackPreviewResponse;
  searchParams: URLSearchParams;
  orgId: string;
  siteId: string;
}): Promise<ReliabilityProofPackPreviewResponse> {
  const { preview, searchParams, orgId, siteId } = args;
  if (!preview?.ok) return preview;
  if (preview.persisted_report?.report_id) return preview;
  const explicitReportId = firstNonEmpty(searchParams.get('report_id'));
  if (explicitReportId) return preview;

  const workflowId = deriveWorkflowIdForProofPreview(preview, searchParams);
  if (!workflowId) return preview;

  const representativeIncidentId = deriveRepresentativeIncidentIdForProofPreview(preview, searchParams);
  const zoneId = firstNonEmpty(
    preview.evidence_pack?.workflow_anchor?.scope?.zone_id,
    searchParams.get('zone_id'),
  );
  const riskType = firstNonEmpty(
    preview.evidence_pack?.workflow_anchor?.scope?.incident_type,
    searchParams.get('policy_type'),
    searchParams.get('incident_type'),
  );
  const start = firstNonEmpty(
    preview.evidence_pack?.workflow_anchor?.selected_report_window?.from,
    preview.evidence_pack?.time_range?.from,
    searchParams.get('report_from'),
    searchParams.get('from_date'),
  );
  const end = firstNonEmpty(
    preview.evidence_pack?.workflow_anchor?.selected_report_window?.to,
    preview.evidence_pack?.time_range?.to,
    searchParams.get('report_to'),
    searchParams.get('to_date'),
  );

  try {
    const response = await reportsApi.createOrResumeSafetyReportDraft(workflowId, {
      representative_incident_id: representativeIncidentId || undefined,
      scope: {
        site_id: siteId,
        zones: zoneId ? [zoneId] : [],
        risk_type: riskType || undefined,
      },
      time_window: {
        start: start || undefined,
        end: end || undefined,
      },
      report_payload: {
        source_model: 'evidence_pack_preview',
        generated_at: preview.evidence_pack?.generated_at,
        summary_sentence: preview.evidence_pack?.report_projection?.customer_summary?.summary_sentence,
        org_id: orgId,
        site_id: siteId,
      },
    });
    return response?.report
      ? {
          ...preview,
          persisted_report: response.report,
        }
      : preview;
  } catch {
    return preview;
  }
}

/** True when a Safety Report preview was already built (download should not regenerate). */
export function hasBuiltProofPackPreview(
  preview: ReliabilityProofPackPreviewResponse | null | undefined,
): boolean {
  if (!preview || typeof preview !== 'object') return false;
  if (preview.ok === true) return true;
  if (preview.proof_readiness) return true;
  const proofPack = preview.evidence_pack?.contents?.reliability_proof_pack;
  return Boolean(proofPack && typeof proofPack === 'object');
}

const PROOF_PACK_BUILD_RETRY_DELAY_MS = 400;
const PROOF_PACK_BUILD_MAX_ATTEMPTS = 2;

export function useIncidentsReports({
  filterCameraId,
  filterZoneId,
  filterDeviceId,
  timeRange,
  searchParams,
  setSearchParams,
  getScopedSiteId,
  getScopedOrgId,
  downloadBlob,
}: UseIncidentsReportsArgs) {
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportTask, setReportTask] = useState<ReportDialogTask>('daily_ops_summary');
  const [reportAdvancedArtifactMode, setReportAdvancedArtifactMode] = useState<boolean>(false);
  const [reportFormMoreOptions, setReportFormMoreOptions] = useState(false);
  const [reportTaskFirstMoreOpen, setReportTaskFirstMoreOpen] = useState(false);
  const [reportKind, setReportKind] = useState<ReportDialogKind>('incidents_rollup');
  const [reportFrom, setReportFrom] = useState<string>(() => resolveInitialReportWindowInput(searchParams.get('report_from')));
  const [reportTo, setReportTo] = useState<string>(() => resolveInitialReportWindowInput(searchParams.get('report_to')));
  const [reportSiteId, setReportSiteId] = useState<string>(() => getScopedSiteId());
  const [reportDeviceId, setReportDeviceId] = useState<string>('');
  const [reportZoneId, setReportZoneId] = useState<string>('');
  const [reportCameraIds, setReportCameraIds] = useState<string[]>([]);
  const [reportPolicyType, setReportPolicyType] = useState<string>('');
  const [reportPolicyIds, setReportPolicyIds] = useState<string[]>([]);
  const [reportScope, setReportScope] = useState<ReliabilityBundleScope>('site');
  const [reportScopeId, setReportScopeId] = useState<string>('');
  const [reportReliabilityFormat, setReportReliabilityFormat] = useState<'pdf' | 'csv' | 'json'>('pdf');
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const [reportNotice, setReportNotice] = useState<string | null>(null);
  const [reportData, setReportData] = useState<IncidentsRollupResponse | null>(null);
  const [reliabilityPreviewData, setReliabilityPreviewData] = useState<ReliabilityBundlePreviewResponse | null>(null);
  const [proofPackPreviewData, setProofPackPreviewData] = useState<ReliabilityProofPackPreviewResponse | null>(null);
  const [craPreviewData, setCraPreviewData] = useState<CraReadinessJsonResponse | null>(null);
  const [siteReadinessPreviewData, setSiteReadinessPreviewData] = useState<SiteReadinessJsonResponse | null>(null);
  const [trustPackJobId, setTrustPackJobId] = useState<string | null>(null);
  const [proofPackJobId, setProofPackJobId] = useState<string | null>(null);
  const [proofPackJobState, setProofPackJobState] = useState<string | null>(null);
  const [proofPackJobLongRunning, setProofPackJobLongRunning] = useState(false);
  const [proofPackDownloading, setProofPackDownloading] = useState(false);
  const [artifactPdfStatus, setArtifactPdfStatus] = useState<'NOT_GENERATED' | 'GENERATING' | 'READY' | 'FAILED'>('NOT_GENERATED');
  const [artifactZipStatus, setArtifactZipStatus] = useState<'NOT_GENERATED' | 'GENERATING' | 'READY' | 'FAILED'>('NOT_GENERATED');
  const [safetyReportTypeMode, setSafetyReportTypeMode] = useState<SafetyReportTypeMode>(
    () => parseSafetyReportTypeFromSearchParams(searchParams).reportTypeMode,
  );
  const [hazardClassKey, setHazardClassKey] = useState<HazardClassKey>(
    () => parseSafetyReportTypeFromSearchParams(searchParams).hazardClassKey,
  );
  const proofScopeCacheKey = useMemo(() => {
    const parts = [
      safetyReportScopeCacheSegment(safetyReportTypeMode, hazardClassKey),
      String(searchParams.get('case_id') || searchParams.get('caseId') || '').trim(),
      String(searchParams.get('incident_id') || '').trim(),
      String(reportZoneId || '').trim(),
      String(reportPolicyType || '').trim(),
      (reportPolicyIds || []).map((value) => String(value || '').trim()).filter(Boolean).sort().join(','),
      (reportCameraIds || []).map((value) => String(value || '').trim()).filter(Boolean).sort().join(','),
      String(reportFrom || '').trim(),
      String(reportTo || '').trim(),
    ].filter(Boolean);
    return parts.join('|');
  }, [
    hazardClassKey,
    reportCameraIds,
    reportFrom,
    reportPolicyIds,
    reportPolicyType,
    reportTo,
    reportZoneId,
    safetyReportTypeMode,
    searchParams,
  ]);
  const [craFormat, setCraFormat] = useState<'json' | 'pdf' | 'zip'>('json');
  const [craTask, setCraTask] = useState<string>('ppe_gloves_goggles_workstation@v0');
  const [craCameraViewId, setCraCameraViewId] = useState<string>('');
  const [craAcceptanceMode, setCraAcceptanceMode] = useState<boolean>(false);
  const [craCaptureProtocolFile, setCraCaptureProtocolFile] = useState<File | null>(null);
  const [craCaptureScenario, setCraCaptureScenario] = useState<string>('');
  const [craUseEdgeModels, setCraUseEdgeModels] = useState<boolean>(false);
  const [craEdgeDeviceId, setCraEdgeDeviceId] = useState<string>('');
  const [craEdgeCameraId, setCraEdgeCameraId] = useState<string>('');
  const [craEdgeMode, setCraEdgeMode] = useState<'live' | 'clip'>('live');
  const [craIncludeEdgeFramesZip, setCraIncludeEdgeFramesZip] = useState<boolean>(false);
  const [craLiveCameraId, setCraLiveCameraId] = useState<string>('');
  const [craCaptureSec, setCraCaptureSec] = useState<string>('120');
  const [craClipFile, setCraClipFile] = useState<File | null>(null);
  const [craPrecomputedFile, setCraPrecomputedFile] = useState<File | null>(null);
  const [craRoiFile, setCraRoiFile] = useState<File | null>(null);
  const [craDeviceMetadataFile, setCraDeviceMetadataFile] = useState<File | null>(null);
  const [craAnnotateFrames, setCraAnnotateFrames] = useState<boolean>(false);
  const [craIncludeRawFrames, setCraIncludeRawFrames] = useState<boolean>(false);
  const [craAnalysisFps, setCraAnalysisFps] = useState<string>('');
  const [craMaxDurationSec, setCraMaxDurationSec] = useState<string>('');
  const [craMaxFrames, setCraMaxFrames] = useState<string>('');
  const [siteReadinessFormat, setSiteReadinessFormat] = useState<'json' | 'pdf' | 'zip'>('json');
  const [siteReadinessMode, setSiteReadinessMode] = useState<'install' | 'audit' | 'precheck'>('install');
  const [siteReadinessTaskProfile, setSiteReadinessTaskProfile] = useState<string>('ppe_gloves_goggles_workstation@v0');
  const [siteReadinessSiteProfileFile, setSiteReadinessSiteProfileFile] = useState<File | null>(null);
  const [siteReadinessCamerasFile, setSiteReadinessCamerasFile] = useState<File | null>(null);
  const [siteReadinessRtspStabilitySec, setSiteReadinessRtspStabilitySec] = useState<string>('');
  const [siteReadinessRtspJitterFailMs, setSiteReadinessRtspJitterFailMs] = useState<string>('');
  const [siteReadinessSwitchModel, setSiteReadinessSwitchModel] = useState<string>('');
  const [siteReadinessPoeTotalWatts, setSiteReadinessPoeTotalWatts] = useState<string>('');
  const [siteReadinessPoePortClass, setSiteReadinessPoePortClass] = useState<string>('');
  const [siteReadinessPoePorts, setSiteReadinessPoePorts] = useState<string>('');
  const [siteReadinessPoweredCameras, setSiteReadinessPoweredCameras] = useState<string>('');
  const [siteReadinessCamerasTransport, setSiteReadinessCamerasTransport] = useState<'wired' | 'wifi' | 'mixed'>('wired');
  const [siteReadinessRfRssiDb, setSiteReadinessRfRssiDb] = useState<string>('');
  const [siteReadinessRfChannel, setSiteReadinessRfChannel] = useState<string>('');
  const [siteReadinessRfPacketLossPct, setSiteReadinessRfPacketLossPct] = useState<string>('');
  const [siteReadinessRfMeasurementMethod, setSiteReadinessRfMeasurementMethod] = useState<string>('manual');
  const [reportSignalView, setReportSignalView] = useState<'ranked' | 'safety_critical' | 'operational' | 'other'>('ranked');
  const [reportSuppressionView, setReportSuppressionView] = useState<'reasons' | 'rules' | 'status'>('reasons');
  const [rulePolicyLoading, setRulePolicyLoading] = useState(false);
  const [rulePolicySaving, setRulePolicySaving] = useState(false);
  const [rulePolicyError, setRulePolicyError] = useState<string | null>(null);
  const [rulePolicyVersion, setRulePolicyVersion] = useState<number>(0);
  const [rulePolicyFromDb, setRulePolicyFromDb] = useState<boolean>(false);
  const [rulePolicyVersions, setRulePolicyVersions] = useState<Array<any>>([]);
  const [rulePolicyVersionsLoading, setRulePolicyVersionsLoading] = useState<boolean>(false);
  const [rulePolicyPreview, setRulePolicyPreview] = useState<IncidentsRulePolicyPreviewResponse | null>(null);
  const [rulePolicyPreviewLoading, setRulePolicyPreviewLoading] = useState<boolean>(false);
  const [ruleTopAreasLimit, setRuleTopAreasLimit] = useState<string>('3');
  const [ruleTopTypesLimit, setRuleTopTypesLimit] = useState<string>('3');
  const [ruleTopActionsLimit, setRuleTopActionsLimit] = useState<string>('3');
  const [rulePpePerHourRed, setRulePpePerHourRed] = useState<string>('5');
  const [ruleDwellTrendAmber, setRuleDwellTrendAmber] = useState<string>('2.0');
  const [ruleHighCriticalRed, setRuleHighCriticalRed] = useState<string>('10');
  const [ruleNoIncidentsGrey, setRuleNoIncidentsGrey] = useState<boolean>(true);
  const [ruleMustActTypes, setRuleMustActTypes] = useState<string>(
    'ppe_violation,zone_breach,zone_violation,restricted_zone,fall_detection,near_miss,forklift_person',
  );
  const [ruleSafetyCriticalTypes, setRuleSafetyCriticalTypes] = useState<string>(
    'ppe_violation,zone_breach,zone_violation,restricted_zone,fall_detection,near_miss,forklift_person',
  );
  const [ruleOperationalTypes, setRuleOperationalTypes] = useState<string>(
    'dwell_violation,loitering,queue_build_up,occupancy_threshold',
  );

  const reportFieldVisibility = useMemo(() => getReportDialogFieldVisibility(reportKind), [reportKind]);
  const taskRequiresAdvancedArtifactMode = useMemo(() => isAdvancedOnlyArtifactTask(reportTask), [reportTask]);
  const showTaskFirstMoreOptions = useMemo(
    () =>
      !reportAdvancedArtifactMode &&
      (
        reportFieldVisibility.usesSiteField ||
        reportFieldVisibility.usesTimeWindow ||
        reportFieldVisibility.showsIncidentFilters ||
        reportFieldVisibility.showsReliabilityFilters ||
        reportFieldVisibility.showsProofPackFilters
      ),
    [reportAdvancedArtifactMode, reportFieldVisibility],
  );

  useEffect(() => {
    if (!trustPackJobId) return;
    let cancelled = false;
    let timer: number | undefined;

    const poll = async () => {
      try {
        const status = await reportsApi.getTrustPackJobStatus(trustPackJobId);
        if (cancelled) return;
        const state = String(status?.status || '').trim().toLowerCase();

        if (state === 'completed' && status?.ok) {
          const { blob, filename } = await reportsApi.downloadTrustPackJob(trustPackJobId);
          if (cancelled) return;
          downloadBlob(blob, filename);
          setReportNotice(`${REPORT_KIND_LABELS.trust_pack} generated. Downloaded ${filename}`);
          setTrustPackJobId(null);
          return;
        }

        if (state === 'error' || state === 'cancelled') {
          setReportError(
            String(status?.detail || status?.error || `Trust pack build ${state}.`)
          );
          setTrustPackJobId(null);
          return;
        }

        setReportNotice(
          `${REPORT_KIND_LABELS.trust_pack} is ${state || 'running'}... this can take several minutes.`
        );
        timer = window.setTimeout(() => {
          void poll();
        }, 2000);
      } catch (e: any) {
        if (cancelled) return;
        setReportError(
          String(e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Failed to track trust pack build')
        );
        setTrustPackJobId(null);
      }
    };

    void poll();
    return () => {
      cancelled = true;
      if (timer != null) window.clearTimeout(timer);
    };
  }, [downloadBlob, trustPackJobId]);

  useEffect(() => {
    if (!proofPackJobId) return;
    let cancelled = false;
    let timer: number | undefined;
    const startedAtMs = Date.now();

    const poll = async () => {
      try {
        const job = await reportsApi.getAsyncReportJob(proofPackJobId, { include_result: true });
        if (cancelled) return;
        const state = String(job?.job?.status || '').trim().toLowerCase();
        const elapsedMs = Date.now() - startedAtMs;
        setProofPackJobState(state || 'running');
        setProofPackJobLongRunning(elapsedMs >= 120_000);

        if (state === 'completed' || state === 'partial') {
          let proofPreview = job?.result || null;
          if (proofPreview?.ok) {
            proofPreview = await attachPersistedSafetyReportDraft({
              preview: proofPreview,
              searchParams,
              orgId: getScopedOrgId(),
              siteId: getScopedSiteId(),
            });
          }
          setProofPackPreviewData(proofPreview);
          if (proofPreview?.ok) {
            writeAcceptanceProofCache(getScopedOrgId(), getScopedSiteId(), proofPreview, reportDeviceId || undefined, proofScopeCacheKey);
          }
          if (!proofPreview?.ok) {
            setReportError('Evidence package preview generation failed.');
          } else {
            setReportNotice('Evidence package preview generated in the background. Acceptance should now reflect proof availability.');
          }
          setProofPackJobId(null);
          setProofPackJobState(null);
          setProofPackJobLongRunning(false);
          return;
        }

        if (state === 'failed' || state === 'canceled') {
          setReportError(
            describeProofPackStartError(
              {
                error: String(job?.job?.error || '').trim() || undefined,
                detail: String(job?.job?.error || '').trim() || undefined,
                status_code: job?.job?.result_status_code ?? undefined,
              },
              job,
            ),
          );
          setProofPackJobId(null);
          setProofPackJobState(null);
          setProofPackJobLongRunning(false);
          return;
        }
        timer = window.setTimeout(() => {
          void poll();
        }, 2000);
      } catch (e: any) {
        if (cancelled) return;
        setReportError(
          String(e?.response?.data?.error || e?.message || 'Failed to track evidence package generation')
        );
        setProofPackJobId(null);
        setProofPackJobState(null);
        setProofPackJobLongRunning(false);
      }
    };

    void poll();
    return () => {
      cancelled = true;
      if (timer != null) window.clearTimeout(timer);
    };
  }, [getScopedOrgId, getScopedSiteId, proofPackJobId, proofScopeCacheKey, reportDeviceId, searchParams]);

  const _openReportLink = useCallback((raw: any) => {
    const s = String(raw || '').trim();
    if (!s) return;
    try {
      const dashboardBaseRaw = String((reportData as any)?.incident_url_base || '').trim();
      const dashboardBase = dashboardBaseRaw || window.location.origin;
      const url = new URL(s, dashboardBase);
      const allowedOrigin = new URL(dashboardBase, window.location.origin).origin;
      const path = String(url.pathname || '');
      const pathAllowed =
        path === '/incidents' ||
        path === '/acceptance/proof' ||
        path === '/acceptance/proof/technical' ||
        path === '/live-view' ||
        path === '/diagnostics' ||
        /^\/devices\/[^/]+\/control$/.test(path);
      if (url.origin !== allowedOrigin || !pathAllowed) {
        setReportNotice('Blocked unsafe report link target.');
        return;
      }
      window.open(url.toString(), '_blank', 'noopener,noreferrer');
    } catch {
      setReportNotice('Could not open report action link.');
    }
  }, [reportData]);

  const _riskFlagSeverity = useCallback((levelRaw: any): 'error' | 'warning' | 'info' => {
    const level = String(levelRaw || '').trim().toLowerCase();
    if (level === 'red') return 'error';
    if (level === 'amber') return 'warning';
    return 'info';
  }, []);

  const _applyRulePolicyToUi = useCallback((policyRaw: IncidentsRulePolicyDoc | null | undefined) => {
    const p = (policyRaw && typeof policyRaw === 'object' ? policyRaw : {}) as IncidentsRulePolicyDoc;
    const limits = (p.limits && typeof p.limits === 'object' ? p.limits : {}) as any;
    const thresholds = (p.thresholds && typeof p.thresholds === 'object' ? p.thresholds : {}) as any;
    const rules = (p.rules && typeof p.rules === 'object' ? p.rules : {}) as any;
    const classification = (p.classification && typeof p.classification === 'object' ? p.classification : {}) as any;
    const mustAct = Array.isArray(p.must_act_incident_types)
      ? p.must_act_incident_types.map((x: any) => String(x || '').trim().toLowerCase()).filter(Boolean)
      : [];
    const safetyCritical = Array.isArray(classification.safety_critical_types)
      ? classification.safety_critical_types.map((x: any) => String(x || '').trim().toLowerCase()).filter(Boolean)
      : [];
    const operational = Array.isArray(classification.operational_types)
      ? classification.operational_types.map((x: any) => String(x || '').trim().toLowerCase()).filter(Boolean)
      : [];
    setRuleTopAreasLimit(String(limits.top_problem_areas ?? 3));
    setRuleTopTypesLimit(String(limits.top_problem_types ?? 3));
    setRuleTopActionsLimit(String(limits.top_actions ?? 3));
    setRulePpePerHourRed(String(thresholds.ppe_red_per_hour_by_zone ?? 5));
    setRuleDwellTrendAmber(String(thresholds.dwell_amber_trend_multiplier ?? 2.0));
    setRuleHighCriticalRed(String(thresholds.high_critical_red_count ?? 10));
    setRuleNoIncidentsGrey(Boolean(rules.no_incidents_scope_grey ?? true));
    setRuleMustActTypes(
      mustAct.length
        ? mustAct.join(',')
        : 'ppe_violation,zone_breach,zone_violation,restricted_zone,fall_detection,near_miss,forklift_person',
    );
    setRuleSafetyCriticalTypes(
      safetyCritical.length
        ? safetyCritical.join(',')
        : 'ppe_violation,zone_breach,zone_violation,restricted_zone,fall_detection,near_miss,forklift_person',
    );
    setRuleOperationalTypes(
      operational.length ? operational.join(',') : 'dwell_violation,loitering,queue_build_up,occupancy_threshold',
    );
  }, []);

  const _loadRulePolicy = useCallback(async () => {
    const siteId = String(reportSiteId || getScopedSiteId()).trim() || 'default';
    const orgId = getScopedOrgId();
    try {
      setRulePolicyLoading(true);
      setRulePolicyVersionsLoading(true);
      setRulePolicyError(null);
      setRulePolicyPreview(null);
      const [activeRes, versionsRes] = await Promise.all([
        reportsApi.getActiveIncidentsRulePolicy({
          org_id: orgId,
          site_id: siteId,
          device_id: reportDeviceId || undefined,
        }),
        reportsApi.listIncidentsRulePolicyVersions({
          org_id: orgId,
          site_id: siteId,
          device_id: reportDeviceId || undefined,
          limit: 100,
        }),
      ]);
      setRulePolicyVersion(Number(activeRes?.version || 0));
      setRulePolicyFromDb(Boolean(activeRes?.from_db));
      _applyRulePolicyToUi(activeRes?.policy || {});
      setRulePolicyVersions(Array.isArray(versionsRes?.items) ? versionsRes.items : []);
    } catch (e: any) {
      setRulePolicyError(String(e?.response?.data?.error || e?.message || 'Failed to load rule policy'));
    } finally {
      setRulePolicyLoading(false);
      setRulePolicyVersionsLoading(false);
    }
  }, [_applyRulePolicyToUi, getScopedOrgId, getScopedSiteId, reportDeviceId, reportSiteId]);

  const _reloadRulePolicyVersionsOnly = useCallback(async () => {
    const siteId = String(reportSiteId || getScopedSiteId()).trim() || 'default';
    const orgId = getScopedOrgId();
    try {
      setRulePolicyVersionsLoading(true);
      const res = await reportsApi.listIncidentsRulePolicyVersions({
        org_id: orgId,
        site_id: siteId,
        device_id: reportDeviceId || undefined,
        limit: 100,
      });
      setRulePolicyVersions(Array.isArray(res?.items) ? res.items : []);
    } catch (e: any) {
      setRulePolicyError(String(e?.response?.data?.error || e?.message || 'Failed to load rule policy'));
    } finally {
      setRulePolicyVersionsLoading(false);
    }
  }, [getScopedOrgId, getScopedSiteId, reportDeviceId, reportSiteId]);

  const _buildRulePolicyFromUi = useCallback((): IncidentsRulePolicyDoc => {
    const parseNum = (raw: string, fallback: number) => {
      const n = Number(raw);
      return Number.isFinite(n) ? n : fallback;
    };
    const mustAct = String(ruleMustActTypes || '')
      .split(',')
      .map((x) => String(x || '').trim().toLowerCase())
      .filter(Boolean)
      .filter((v, i, arr) => arr.indexOf(v) === i);
    const safetyCritical = String(ruleSafetyCriticalTypes || '')
      .split(',')
      .map((x) => String(x || '').trim().toLowerCase())
      .filter(Boolean)
      .filter((v, i, arr) => arr.indexOf(v) === i);
    const operational = String(ruleOperationalTypes || '')
      .split(',')
      .map((x) => String(x || '').trim().toLowerCase())
      .filter(Boolean)
      .filter((v, i, arr) => arr.indexOf(v) === i);
    return {
      limits: {
        top_problem_areas: Math.max(1, Math.min(20, Math.round(parseNum(ruleTopAreasLimit, 3)))),
        top_problem_types: Math.max(1, Math.min(20, Math.round(parseNum(ruleTopTypesLimit, 3)))),
        top_actions: Math.max(1, Math.min(20, Math.round(parseNum(ruleTopActionsLimit, 3)))),
      },
      thresholds: {
        ppe_red_per_hour_by_zone: Math.max(0.1, Math.min(10000, parseNum(rulePpePerHourRed, 5))),
        dwell_amber_trend_multiplier: Math.max(1.0, Math.min(100, parseNum(ruleDwellTrendAmber, 2.0))),
        high_critical_red_count: Math.max(1, Math.min(1000000, Math.round(parseNum(ruleHighCriticalRed, 10)))),
      },
      rules: {
        no_incidents_scope_grey: Boolean(ruleNoIncidentsGrey),
      },
      classification: {
        safety_critical_types: safetyCritical,
        operational_types: operational,
      },
      must_act_incident_types: mustAct,
    };
  }, [
    ruleDwellTrendAmber,
    ruleHighCriticalRed,
    ruleMustActTypes,
    ruleNoIncidentsGrey,
    ruleOperationalTypes,
    rulePpePerHourRed,
    ruleSafetyCriticalTypes,
    ruleTopActionsLimit,
    ruleTopAreasLimit,
    ruleTopTypesLimit,
  ]);

  const _saveRulePolicy = useCallback(async (activateNow: boolean) => {
    const siteId = String(reportSiteId || getScopedSiteId()).trim() || 'default';
    const orgId = getScopedOrgId();
    const policy: IncidentsRulePolicyDoc = _buildRulePolicyFromUi();
    try {
      setRulePolicySaving(true);
      setRulePolicyError(null);
      const res = await reportsApi.saveIncidentsRulePolicy({
        org_id: orgId,
        site_id: siteId,
        policy,
        activate: activateNow,
      });
      setRulePolicyVersion(Number(res?.version || rulePolicyVersion || 0));
      setRulePolicyFromDb(Boolean(res?.from_db ?? true));
      _applyRulePolicyToUi(res?.policy || policy);
      setRulePolicyPreview(null);
      setReportNotice(
        activateNow
          ? `Rule policy saved and activated (v${Number(res?.version || 0)}).`
          : `Rule policy draft saved (v${Number(res?.version || 0)}).`,
      );
      await _reloadRulePolicyVersionsOnly();
    } catch (e: any) {
      setRulePolicyError(String(e?.response?.data?.error || e?.message || 'Failed to save rule policy'));
    } finally {
      setRulePolicySaving(false);
    }
  }, [
    _applyRulePolicyToUi,
    _buildRulePolicyFromUi,
    _reloadRulePolicyVersionsOnly,
    getScopedOrgId,
    getScopedSiteId,
    reportSiteId,
    rulePolicyVersion,
  ]);

  const _activateRulePolicyVersion = useCallback(async (version: number) => {
    const siteId = String(reportSiteId || getScopedSiteId()).trim() || 'default';
    const orgId = getScopedOrgId();
    try {
      setRulePolicySaving(true);
      setRulePolicyError(null);
      const res = await reportsApi.activateIncidentsRulePolicyVersion({
        org_id: orgId,
        site_id: siteId,
        version: Number(version || 0),
      });
      setRulePolicyVersion(Number(res?.version || version || 0));
      setRulePolicyFromDb(Boolean(res?.from_db ?? true));
      _applyRulePolicyToUi(res?.policy || {});
      setRulePolicyPreview(null);
      setReportNotice(`Activated rule policy v${Number(res?.version || version || 0)}.`);
      await _reloadRulePolicyVersionsOnly();
    } catch (e: any) {
      setRulePolicyError(String(e?.response?.data?.error || e?.message || 'Failed to activate rule policy'));
    } finally {
      setRulePolicySaving(false);
    }
  }, [_applyRulePolicyToUi, _reloadRulePolicyVersionsOnly, getScopedOrgId, getScopedSiteId, reportSiteId]);

  const _deactivateRulePolicy = useCallback(async () => {
    const siteId = String(reportSiteId || getScopedSiteId()).trim() || 'default';
    const orgId = getScopedOrgId();
    try {
      setRulePolicySaving(true);
      setRulePolicyError(null);
      const res = await reportsApi.deactivateIncidentsRulePolicy({
        org_id: orgId,
        site_id: siteId,
      });
      setRulePolicyVersion(Number(res?.active_version || 0));
      setRulePolicyFromDb(Boolean(res?.active_from_db));
      _applyRulePolicyToUi((res as any)?.policy || {});
      setRulePolicyPreview(null);
      setReportNotice('Active site policy deactivated. Fallback defaults are now in effect.');
      await _reloadRulePolicyVersionsOnly();
    } catch (e: any) {
      setRulePolicyError(String(e?.response?.data?.error || e?.message || 'Failed to deactivate rule policy'));
    } finally {
      setRulePolicySaving(false);
    }
  }, [_applyRulePolicyToUi, _reloadRulePolicyVersionsOnly, getScopedOrgId, getScopedSiteId, reportSiteId]);

  const _previewRulePolicyImpact = useCallback(async () => {
    const fromIso = toIsoFromLocalInput(reportFrom);
    const toIso = toIsoFromLocalInput(reportTo);
    const siteId = String(reportSiteId || getScopedSiteId()).trim() || 'default';
    const orgId = getScopedOrgId();
    if (!fromIso || !toIso) {
      setRulePolicyError('Valid report From/To is required for preview.');
      return;
    }
    const policy: IncidentsRulePolicyDoc = _buildRulePolicyFromUi();
    try {
      setRulePolicyPreviewLoading(true);
      setRulePolicyError(null);
      const res = await reportsApi.previewIncidentsRulePolicy({
        org_id: orgId,
        site_id: siteId,
        from_date: fromIso,
        to_date: toIso,
        device_id: reportDeviceId || undefined,
        zone_id: reportZoneId || undefined,
        camera_ids: reportCameraIds || [],
        policy_ids: reportPolicyIds || [],
        policy_type: reportPolicyType || undefined,
        policy,
      });
      setRulePolicyPreview(res || null);
      const added = Array.isArray(res?.delta?.risk_flags_added) ? res?.delta?.risk_flags_added?.length || 0 : 0;
      const removed = Array.isArray(res?.delta?.risk_flags_removed) ? res?.delta?.risk_flags_removed?.length || 0 : 0;
      setReportNotice(`Policy preview ready (flags added=${added}, removed=${removed}).`);
    } catch (e: any) {
      setRulePolicyError(String(e?.response?.data?.error || e?.message || 'Failed to preview rule policy'));
    } finally {
      setRulePolicyPreviewLoading(false);
    }
  }, [
    _buildRulePolicyFromUi,
    getScopedOrgId,
    getScopedSiteId,
    reportCameraIds,
    reportDeviceId,
    reportFrom,
    reportPolicyIds,
    reportPolicyType,
    reportSiteId,
    reportTo,
    reportZoneId,
  ]);

  const _openReportDialog = useCallback((opts?: {
    kind?: ReportDialogKind | null;
    task?: ReportDialogTask | null;
    deviceId?: string | null;
    siteId?: string | null;
    zoneId?: string | null;
    cameraId?: string | null;
    policyType?: string | null;
    policyIds?: string[] | null;
    advancedArtifactPreset?: boolean | null;
  }) => {
    const reportKindPreset = opts?.kind || 'incidents_rollup';
    const reportTaskPreset = opts?.task || inferReportTask(reportKindPreset);
    const advancedArtifactPreset = Boolean(
      opts?.advancedArtifactPreset ??
        Boolean(
          (opts?.kind && !opts?.task) ||
            reportTaskPreset === 'camera_acceptance' ||
            reportTaskPreset === 'site_acceptance',
        ),
    );
    const filteredCameraId = String(filterCameraId || '').trim();
    const filteredZoneId = String(filterZoneId || '').trim();
    const filteredDeviceId = String(filterDeviceId || '').trim();
    const prefillDeviceId = String(opts?.deviceId || '').trim();
    const effectiveDeviceId = prefillDeviceId || filteredDeviceId;
    const effectiveZoneId = String(opts?.zoneId ?? '').trim() || filteredZoneId;
    const effectiveCameraId = String(opts?.cameraId ?? '').trim() || filteredCameraId;
    const presetPolicyIds = Array.isArray(opts?.policyIds) ? (opts?.policyIds ?? []) : [];
    const inferredScope: ReliabilityBundleScope = effectiveCameraId
      ? 'camera'
      : effectiveZoneId
        ? 'zone'
        : effectiveDeviceId
          ? 'device'
          : 'site';
    const inferredScopeId =
      inferredScope === 'camera'
        ? effectiveCameraId
        : inferredScope === 'zone'
          ? effectiveZoneId
          : inferredScope === 'device'
            ? effectiveDeviceId
            : '';
    const scopedSite = String(opts?.siteId || '').trim() || getScopedSiteId();
    const proofCacheScopeKey = [
      String(searchParams.get('case_id') || searchParams.get('caseId') || '').trim(),
      String(searchParams.get('incident_id') || '').trim(),
      effectiveZoneId,
      String(opts?.policyType || '').trim(),
      presetPolicyIds.map((value) => String(value || '').trim()).filter(Boolean).sort().join(','),
      effectiveCameraId,
      toLocalInputValue(timeRange.fromDate || ''),
      toLocalInputValue(timeRange.toDate || ''),
    ].filter(Boolean).join('|');
    setReportError(null);
    setReportNotice(null);
    setReportData(null);
    setReliabilityPreviewData(null);
    if (reportKindPreset === 'proof_pack') {
      const orgIdForCache = String(getScopedOrgId() || '').trim();
      const siteIdForCache = String(scopedSite || '').trim();
      const deviceIdForCache = String(effectiveDeviceId || '').trim() || undefined;
      const cachedProof =
        orgIdForCache && siteIdForCache
          ? readAcceptanceProofCache(orgIdForCache, siteIdForCache, deviceIdForCache, proofCacheScopeKey)
          : null;
      setProofPackPreviewData(cachedProof?.ok ? cachedProof : null);
    } else {
      setProofPackPreviewData(null);
    }
    setCraPreviewData(null);
    setSiteReadinessPreviewData(null);
    setRulePolicyPreview(null);
    setReportAdvancedArtifactMode(advancedArtifactPreset);
    setReportTask(reportTaskPreset);
    setReportKind(reportKindPreset);
    setReportSiteId(scopedSite);
    setReportDeviceId(effectiveDeviceId);
    setReportZoneId(effectiveZoneId);
    setReportCameraIds(effectiveCameraId ? [effectiveCameraId] : []);
    setReportPolicyType(String(opts?.policyType || '').trim());
    setReportPolicyIds(
      presetPolicyIds.map((value) => String(value || '').trim())
        .filter(Boolean),
    );
    setReportScope(inferredScope);
    setReportScopeId(inferredScopeId);
    setReportReliabilityFormat('pdf');
    setCraFormat('json');
    setCraTask('ppe_gloves_goggles_workstation@v0');
    setCraCameraViewId('');
    setCraUseEdgeModels(false);
    setCraEdgeDeviceId('');
    setCraEdgeCameraId('');
    setCraEdgeMode('live');
    setCraIncludeEdgeFramesZip(false);
    setCraLiveCameraId('');
    setCraCaptureSec('120');
    setCraAcceptanceMode(false);
    setCraCaptureProtocolFile(null);
    setCraCaptureScenario('');
    setCraClipFile(null);
    setCraPrecomputedFile(null);
    setCraRoiFile(null);
    setCraDeviceMetadataFile(null);
    setCraAnnotateFrames(false);
    setCraIncludeRawFrames(false);
    setCraAnalysisFps('');
    setCraMaxDurationSec('');
    setCraMaxFrames('');
    setSiteReadinessFormat('json');
    setSiteReadinessMode('install');
    setSiteReadinessTaskProfile('ppe_gloves_goggles_workstation@v0');
    setSiteReadinessSiteProfileFile(null);
    setSiteReadinessCamerasFile(null);
    setSiteReadinessRtspStabilitySec('');
    setSiteReadinessRtspJitterFailMs('');
    setSiteReadinessSwitchModel('');
    setSiteReadinessPoeTotalWatts('');
    setSiteReadinessPoePortClass('');
    setSiteReadinessPoePorts('');
    setSiteReadinessPoweredCameras('');
    setSiteReadinessCamerasTransport('wired');
    setSiteReadinessRfRssiDb('');
    setSiteReadinessRfChannel('');
    setSiteReadinessRfPacketLossPct('');
    setSiteReadinessRfMeasurementMethod('manual');
    setReportFrom(toLocalInputValue(timeRange.fromDate || ''));
    setReportTo(toLocalInputValue(timeRange.toDate || ''));
    setReportTaskFirstMoreOpen(false);
    setReportFormMoreOptions(false);
    setReportDialogOpen(true);
  }, [filterCameraId, filterDeviceId, filterZoneId, getScopedOrgId, getScopedSiteId, searchParams, timeRange.fromDate, timeRange.toDate]);

  useEffect(() => {
    const preset = resolveArtifactFlowPreset(searchParams);
    if (!preset) return;
    _openReportDialog({
      kind: preset.kind,
      task: preset.advancedArtifactPreset ? undefined : preset.task,
      deviceId: preset.deviceId,
      siteId: preset.siteId,
      zoneId: preset.zoneId,
      cameraId: preset.cameraId,
      policyType: preset.policyType,
      policyIds: preset.policyId ? [preset.policyId] : [],
      advancedArtifactPreset: preset.advancedArtifactPreset,
    });
    if (preset.reportFrom) setReportFrom(preset.reportFrom);
    if (preset.reportTo) setReportTo(preset.reportTo);
    const next = new URLSearchParams(searchParams);
    next.delete('open_report');
    next.delete('report_kind');
    next.delete('report_task');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    setReportError(null);
    setReportNotice(null);
    if (reportKind !== 'incidents_rollup') setReportData(null);
    if (reportKind !== 'reliability_bundle') setReliabilityPreviewData(null);
    if (reportKind !== 'proof_pack') setProofPackPreviewData(null);
    if (reportKind !== 'cra_readiness') setCraPreviewData(null);
    if (reportKind !== 'site_readiness') setSiteReadinessPreviewData(null);
    if (reportKind !== 'incidents_rollup') {
      setRulePolicyPreview(null);
      setRulePolicyVersions([]);
    }
    if (reportKind === 'incidents_rollup') {
      setReportSignalView('ranked');
      setReportSuppressionView('reasons');
    }
  }, [reportKind]);

  useEffect(() => {
    if (!reportAdvancedArtifactMode) {
      const recommendedKind = REPORT_TASK_TO_RECOMMENDED_KIND[reportTask];
      if (reportKind !== recommendedKind) {
        setReportKind(recommendedKind);
      }
    }
  }, [reportAdvancedArtifactMode, reportKind, reportTask]);

  useEffect(() => {
    if (!reportDialogOpen) return;
    if (reportKind !== 'incidents_rollup') return;
    void _loadRulePolicy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportDialogOpen, reportKind, reportSiteId, reportDeviceId]);

  const _downloadCsv = useCallback(async () => {
    const fromIso = toIsoFromLocalInput(reportFrom);
    const toIso = toIsoFromLocalInput(reportTo);
    const siteId = String(reportSiteId || getScopedSiteId()).trim() || 'default';
    const orgId = getScopedOrgId();
    if (!fromIso || !toIso) {
      setReportError('Please provide a valid From/To date range.');
      return;
    }
    const { blob, filename } = await reportsApi.downloadIncidentsRollupCsv({
      from_date: fromIso,
      to_date: toIso,
      org_id: orgId,
      site_id: siteId,
      device_id: reportDeviceId || undefined,
      zone_id: reportZoneId || undefined,
      camera_ids: reportCameraIds || [],
      policy_type: reportPolicyType || undefined,
      policy_ids: reportPolicyIds || [],
    });
    downloadBlob(blob, filename);
  }, [
    downloadBlob,
    getScopedOrgId,
    getScopedSiteId,
    reportCameraIds,
    reportDeviceId,
    reportFrom,
    reportPolicyIds,
    reportPolicyType,
    reportSiteId,
    reportTo,
    reportZoneId,
  ]);

  const _printReport = useCallback(() => {
    if (!reportData?.ok) return;
    const filters = reportData.filters || {};
    const totals = reportData.totals || { total: 0, by_severity: { low: 0, medium: 0, high: 0, critical: 0 } };
    const hotspots = reportData.top_hotspots_by_zone || [];
    const breakdown = reportData.violations_by_zone_camera_hour || [];
    const incidents = reportData.recent_incidents || [];
    const operatorSummary = reportData.operator_summary || {};
    const topAreas = operatorSummary.priority_ranked_areas || operatorSummary.top_problem_areas || [];
    const topTypes =
      operatorSummary?.problem_type_views?.ranked_problem_types || operatorSummary.top_problem_types || [];
    const noiseSignal: any = operatorSummary.noise_signal || {};
    const trend = operatorSummary.since_last_report || null;
    const riskFlags = operatorSummary.risk_flags || [];
    const topActions = operatorSummary.top_actions || [];

    const esc = (v: any) =>
      String(v ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

    const incidentUrlBase = String((reportData as any)?.incident_url_base || '').trim() || window.location.origin;
    const absUrl = (raw: any): string => {
      const s = String(raw || '').trim();
      if (!s) return '';
      try {
        return new URL(s, incidentUrlBase).toString();
      } catch {
        return s;
      }
    };

    const incidentRowsHtml = incidents
      .slice(0, 200)
      .map((i: any) => {
        const href = absUrl(i.url || '');
        return `<tr>
          <td>${esc(i.ts_start || i.created_at || '')}</td>
          <td><a href="${esc(href)}" target="_blank" rel="noopener noreferrer">${esc(i.incident_id || '')}</a></td>
          <td>${esc(i.zone_id || '')}</td>
          <td>${esc(i.camera_id || '')}</td>
          <td>${esc(i.severity || '')}</td>
        </tr>`;
      })
      .join('');
    const areaRowsHtml = topAreas
      .slice(0, 3)
      .map((h: any) => {
        const links = h?.links || {};
        const incidentsUrl = absUrl(links.incidents || '');
        const liveUrl = absUrl(links.live_view || '');
        const deviceUrl = absUrl(links.device_control || '');
        const actions = [
          incidentsUrl ? `<a href="${esc(incidentsUrl)}" target="_blank" rel="noopener noreferrer">Incidents</a>` : '',
          liveUrl ? `<a href="${esc(liveUrl)}" target="_blank" rel="noopener noreferrer">Live View</a>` : '',
          deviceUrl ? `<a href="${esc(deviceUrl)}" target="_blank" rel="noopener noreferrer">Device Control</a>` : '',
        ]
          .filter(Boolean)
          .join(' | ');
        return `<tr>
          <td>${esc(h.zone_id || '—')}</td>
          <td>${esc(h.camera_id || '—')}</td>
          <td>${incidentsUrl ? `<a href="${esc(incidentsUrl)}" target="_blank" rel="noopener noreferrer">${esc(h.count || 0)}</a>` : esc(h.count || 0)}</td>
          <td>${esc(h.max_severity || '—')}</td>
          <td>${actions || '—'}</td>
        </tr>`;
      })
      .join('');
    const typeRowsHtml = topTypes
      .slice(0, 3)
      .map((t: any) => {
        const incidentsUrl = absUrl(t?.links?.incidents || '');
        return `<tr>
          <td>${esc(t.incident_type || 'unknown')}</td>
          <td>${incidentsUrl ? `<a href="${esc(incidentsUrl)}" target="_blank" rel="noopener noreferrer">${esc(t.count || 0)}</a>` : esc(t.count || 0)}</td>
          <td>${esc(t.max_severity || '—')}</td>
        </tr>`;
      })
      .join('');
    const riskRowsHtml = riskFlags
      .slice(0, 6)
      .map((r: any) => {
        const incidentsUrl = absUrl(r?.links?.incidents || '');
        return `<tr>
          <td>${esc(r.level || 'info')}</td>
          <td>${esc(r.title || '')}</td>
          <td>${esc(r.detail || '')}</td>
          <td>${incidentsUrl ? `<a href="${esc(incidentsUrl)}" target="_blank" rel="noopener noreferrer">Open</a>` : '—'}</td>
        </tr>`;
      })
      .join('');
    const actionsRowsHtml = topActions
      .slice(0, 3)
      .map((a: any) => {
        const incidentsUrl = absUrl(a?.links?.incidents || '');
        const liveUrl = absUrl(a?.links?.live_view || '');
        const deviceUrl = absUrl(a?.links?.device_control || '');
        const actions = [
          incidentsUrl ? `<a href="${esc(incidentsUrl)}" target="_blank" rel="noopener noreferrer">Incidents</a>` : '',
          liveUrl ? `<a href="${esc(liveUrl)}" target="_blank" rel="noopener noreferrer">Live View</a>` : '',
          deviceUrl ? `<a href="${esc(deviceUrl)}" target="_blank" rel="noopener noreferrer">Device Control</a>` : '',
        ]
          .filter(Boolean)
          .join(' | ');
        return `<tr>
          <td>${esc(a.title || '')}</td>
          <td>${esc(a.detail || '')}</td>
          <td>${esc(a.priority || '')}</td>
          <td>${actions || '—'}</td>
        </tr>`;
      })
      .join('');
    const _bucketTypeText = (items: any[]) =>
      (Array.isArray(items) ? items : [])
        .slice(0, 3)
        .map((x: any) => `${String(x?.incident_type || 'unknown')} (${Number(x?.count || 0)})`)
        .join(', ');
    const safetyBucket = noiseSignal?.safety_critical || {};
    const operationalBucket = noiseSignal?.operational || {};
    const otherBucket = noiseSignal?.other || {};
    const suppressedBucket = noiseSignal?.suppressed_or_ignored || {};
    const noiseRowsHtml = [
      {
        label: 'Safety-critical',
        count: Number(safetyBucket?.count || 0),
        pct: Number(safetyBucket?.pct_of_total || 0),
        topTypes: _bucketTypeText(safetyBucket?.top_types || []),
      },
      {
        label: 'Operational',
        count: Number(operationalBucket?.count || 0),
        pct: Number(operationalBucket?.pct_of_total || 0),
        topTypes: _bucketTypeText(operationalBucket?.top_types || []),
      },
      {
        label: 'Other',
        count: Number(otherBucket?.count || 0),
        pct: Number(otherBucket?.pct_of_total || 0),
        topTypes: _bucketTypeText(otherBucket?.top_types || []),
      },
      {
        label: 'Suppressed / ignored',
        count: Number(suppressedBucket?.count || 0),
        pct: Number(suppressedBucket?.pct_of_total || 0),
        topTypes: `suppressed=${Number(suppressedBucket?.suppressed_count || 0)}, ignored=${Number(
          suppressedBucket?.ignored_count || 0,
        )} (${String(suppressedBucket?.mode || 'unavailable')})`,
      },
    ]
      .map(
        (r) => `<tr>
          <td>${esc(r.label)}</td>
          <td>${esc(r.count)}</td>
          <td>${esc(Number(r.pct || 0).toFixed(2))}%</td>
          <td>${esc(r.topTypes || '—')}</td>
        </tr>`,
      )
      .join('');

    const w = window.open('', '_blank');
    if (!w) return;
    const title = 'Incidents Rollup Report';
    const html = `
<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${esc(title)}</title>
    <style>
      body { font-family: Inter, Arial, sans-serif; margin: 24px; color: #111; }
      h1 { margin: 0 0 8px 0; font-size: 20px; }
      .muted { color: #555; font-size: 12px; margin-bottom: 16px; }
      table { border-collapse: collapse; width: 100%; margin: 12px 0 18px 0; }
      th, td { border: 1px solid #ddd; padding: 6px 8px; font-size: 12px; }
      th { background: #f5f5f5; text-align: left; }
      .grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
      .card { border: 1px solid #ddd; border-radius: 8px; padding: 12px; }
      .k { font-size: 12px; color: #555; }
      .v { font-size: 18px; font-weight: 700; }
      a { color: #1565c0; text-decoration: none; }
      @media print { a { color: #000; text-decoration: underline; } }
    </style>
  </head>
  <body>
    <h1>${esc(title)}</h1>
    <div class="muted">
      Site: ${esc(filters.site_id || 'default')} | Window: ${esc(filters.from_date || '')} → ${esc(filters.to_date || '')}
      ${filters.zone_id ? `| Zone: ${esc(filters.zone_id)}` : ''}
    </div>

    <div class="grid">
      <div class="card"><div class="k">Total incidents</div><div class="v">${esc(totals.total)}</div></div>
      <div class="card"><div class="k">By severity</div><div class="v" style="font-size:14px;">
        Critical: ${esc(totals.by_severity.critical)} | High: ${esc(totals.by_severity.high)} | Medium: ${esc(totals.by_severity.medium)} | Low: ${esc(totals.by_severity.low)}
      </div></div>
    </div>

    <h2 style="font-size:14px;margin-top:18px;">Operator Summary</h2>
    ${
      trend
        ? `<div class="muted">Since last report: ${esc(trend.delta_count >= 0 ? '+' : '')}${esc(trend.delta_count)} incidents (${esc(trend.delta_pct)}%, ${esc(trend.direction)}), previous window ${esc(trend.previous_from_date || '')} → ${esc(trend.previous_to_date || '')}</div>`
        : ''
    }
    <h3 style="font-size:13px;margin:10px 0 4px 0;">Top problem areas (zone + camera)</h3>
    <table>
      <thead><tr><th>Zone</th><th>Camera</th><th>Count</th><th>Max severity</th><th>Actions</th></tr></thead>
      <tbody>
        ${areaRowsHtml || '<tr><td colspan="5">No data</td></tr>'}
      </tbody>
    </table>

    <h3 style="font-size:13px;margin:10px 0 4px 0;">Top problem types</h3>
    <table>
      <thead><tr><th>Type</th><th>Count</th><th>Max severity</th></tr></thead>
      <tbody>
        ${typeRowsHtml || '<tr><td colspan="3">No data</td></tr>'}
      </tbody>
    </table>

    <h3 style="font-size:13px;margin:10px 0 4px 0;">Signal vs noise</h3>
    <div class="muted">Dominant bucket: ${esc(noiseSignal?.dominant_bucket || 'none')}</div>
    <table>
      <thead><tr><th>Bucket</th><th>Count</th><th>% of total</th><th>Top types / detail</th></tr></thead>
      <tbody>
        ${noiseRowsHtml || '<tr><td colspan="4">No bucket data</td></tr>'}
      </tbody>
    </table>

    <h3 style="font-size:13px;margin:10px 0 4px 0;">SLA risk flags</h3>
    <table>
      <thead><tr><th>Level</th><th>Flag</th><th>Detail</th><th>Action</th></tr></thead>
      <tbody>
        ${riskRowsHtml || '<tr><td colspan="4">No risk flags</td></tr>'}
      </tbody>
    </table>

    <h3 style="font-size:13px;margin:10px 0 4px 0;">Top actions</h3>
    <table>
      <thead><tr><th>Action</th><th>Why now</th><th>Priority</th><th>Links</th></tr></thead>
      <tbody>
        ${actionsRowsHtml || '<tr><td colspan="4">No actions</td></tr>'}
      </tbody>
    </table>

    <h2 style="font-size:14px;margin-top:18px;">Top high-risk areas (by zone)</h2>
    <table>
      <thead><tr><th>Zone</th><th>Count</th><th>Max severity</th></tr></thead>
      <tbody>
        ${hotspots.slice(0, 20).map((h:any)=>`<tr><td>${esc(h.zone_id||'')}</td><td>${esc(h.count||0)}</td><td>${esc(h.max_severity||'')}</td></tr>`).join('')}
      </tbody>
    </table>

    <h2 style="font-size:14px;">Violations by zone/camera/hour</h2>
    <table>
      <thead><tr><th>Zone</th><th>Camera</th><th>Hour (0-23)</th><th>Count</th></tr></thead>
      <tbody>
        ${breakdown.slice(0, 200).map((b:any)=>`<tr><td>${esc(b.zone_id||'')}</td><td>${esc(b.camera_id||'')}</td><td>${esc(b.hour_of_day||'')}</td><td>${esc(b.count||0)}</td></tr>`).join('')}
      </tbody>
    </table>

    <h2 style="font-size:14px;">Incident links (recent)</h2>
    <table>
      <thead><tr><th>Time</th><th>Incident</th><th>Zone</th><th>Camera</th><th>Severity</th></tr></thead>
      <tbody>
        ${incidentRowsHtml}
      </tbody>
    </table>

    <script>window.onload=function(){ window.print(); };</script>
  </body>
</html>
    `.trim();
    try {
      w.document.open();
      w.document.write(html);
      w.document.close();
    } catch {
      // ignore
    }
  }, [reportData]);

  const buildEvidencePackParams = useCallback(() => {
    const { fromIso, toIso } = resolveReportWindowIso(reportFrom, reportTo, searchParams);
    const params = buildSafetyReportEvidencePackFilters({
      fromIso: fromIso as string,
      toIso: toIso as string,
      orgId: getScopedOrgId(),
      siteId: String(reportSiteId || getScopedSiteId()).trim() || 'default',
      deviceId: reportDeviceId || undefined,
      zoneId: reportZoneId || undefined,
      cameraIds: reportCameraIds || [],
      policyType: reportPolicyType || undefined,
      policyIds: reportPolicyIds || [],
      caseId:
        String(searchParams.get('case_id') || searchParams.get('caseId') || '').trim() || undefined,
      incidentId: String(searchParams.get('incident_id') || '').trim() || undefined,
      reportTypeMode: safetyReportTypeMode,
      hazardClassKey,
    });
    const reportId = String(searchParams.get('report_id') || '').trim();
    return reportId ? { ...params, report_id: reportId } : params;
  }, [
    getScopedOrgId,
    getScopedSiteId,
    hazardClassKey,
    reportCameraIds,
    reportDeviceId,
    reportFrom,
    reportPolicyIds,
    reportPolicyType,
    reportSiteId,
    reportTo,
    reportZoneId,
    safetyReportTypeMode,
    searchParams,
  ]);

  const _downloadProofPackAction = useCallback(async () => {
    const params = buildEvidencePackParams();
    if (!params.from_date || !params.to_date) {
      setReportError('Please provide a valid From/To date range.');
      return;
    }
    try {
      setProofPackDownloading(true);
      setReportError(null);
      setReportNotice(null);
      const { blob, filename } = await reportsApi.downloadEvidencePack(params);
      downloadBlob(blob, filename);
      const productName = resolveReportProductName(safetyReportTypeMode, hazardClassKey);
      setReportNotice(`${productName} data package downloaded as ${filename}`);
    } catch (e: any) {
      const productName = resolveReportProductName(safetyReportTypeMode, hazardClassKey);
      setReportError(
        String(e?.response?.data?.error || e?.response?.data?.detail || e?.message || e) ||
          `${productName} download failed.`,
      );
    } finally {
      setProofPackDownloading(false);
    }
  }, [buildEvidencePackParams, downloadBlob, hazardClassKey, safetyReportTypeMode]);

  const downloadPersistedSafetyScanPdf = useCallback(async (
    reportId: string,
    options?: { face?: 'customer_compact' | 'detailed' | null },
  ) => {
    const reportKey = String(reportId || '').trim();
    if (!reportKey) {
      setReportError('Open a Safety Scan before downloading the report PDF.');
      return;
    }
    const face = options?.face === 'detailed' || options?.face === 'customer_compact'
      ? options.face
      : undefined;
    try {
      setProofPackDownloading(true);
      setReportError(null);
      setArtifactPdfStatus('GENERATING');
      setReportNotice(
        face === 'detailed'
          ? 'Downloading detailed signed artifact PDF…'
          : 'Downloading customer scan PDF…',
      );
      const { blob, filename } = await reportsApi.downloadSafetyReportArtifactPdf(
        reportKey,
        face ? { face } : undefined,
      );
      downloadBlob(blob, filename);
      setArtifactPdfStatus('READY');
      setReportNotice(
        face === 'detailed'
          ? `Detailed signed artifact downloaded as ${filename}`
          : `Customer scan PDF downloaded as ${filename}`,
      );
    } catch (e: any) {
      setReportError(
        String(e?.response?.data?.error || e?.response?.data?.detail || e?.message || e) ||
          'Safety Scan PDF download failed.',
      );
      setArtifactPdfStatus('FAILED');
      setReportNotice(null);
    } finally {
      setProofPackDownloading(false);
    }
  }, [downloadBlob]);

  const downloadPersistedSafetyScanPackage = useCallback(async (reportId: string) => {
    const reportKey = String(reportId || '').trim();
    if (!reportKey) {
      setReportError('Open a Safety Scan before downloading the evidence package.');
      return;
    }
    try {
      setProofPackDownloading(true);
      setReportError(null);
      setArtifactZipStatus('GENERATING');
      setReportNotice('Downloading Safety Scan package…');
      try {
        const { blob, filename } = await reportsApi.downloadSafetyReportArtifact(reportKey);
        downloadBlob(blob, filename);
        setArtifactZipStatus('READY');
        setReportNotice(`Safety Scan package downloaded as ${filename}`);
        return;
      } catch (artifactErr: any) {
        const params = buildEvidencePackParams();
        if (!params.from_date || !params.to_date) {
          throw artifactErr;
        }
        setReportNotice('Persisted package not ready yet — downloading Scan Package ZIP…');
        const { blob, filename } = await reportsApi.downloadEvidencePack(params);
        downloadBlob(blob, filename);
        setReportNotice(`Scan Package downloaded as ${filename}`);
        setArtifactZipStatus('READY');
      }
    } catch (e: any) {
      setArtifactZipStatus('FAILED');
      setReportError(
        String(e?.response?.data?.error || e?.response?.data?.detail || e?.message || e) ||
          'Safety Scan package download failed.',
      );
    } finally {
      setProofPackDownloading(false);
    }
  }, [buildEvidencePackParams, downloadBlob]);

  const loadPersistedSafetyReportReadOnly = useCallback(async (reportId: string) => {
    const reportKey = String(reportId || '').trim();
    if (!reportKey) return;
    try {
      setReportError(null);
      const opened = await reportsApi.openSafetyReport(reportKey);
      if (opened?.report) {
        setProofPackPreviewData((prev) => ({
          ...(prev && typeof prev === 'object' ? prev : {}),
          ok: true,
          persisted_report: opened.report as ReliabilityProofPackPreviewResponse['persisted_report'],
        }));
      }
      try {
        const status = await reportsApi.getSafetyReportArtifactStatus(reportKey);
        const pdf = String(status?.pdf?.status || '').trim().toUpperCase();
        const zip = String(status?.zip?.status || '').trim().toUpperCase();
        if (pdf === 'READY' || pdf === 'GENERATING' || pdf === 'FAILED' || pdf === 'NOT_GENERATED') {
          setArtifactPdfStatus(pdf);
        }
        if (zip === 'READY' || zip === 'GENERATING' || zip === 'FAILED' || zip === 'NOT_GENERATED') {
          setArtifactZipStatus(zip);
        }
      } catch {
        // Package status is optional on ordinary Scan load. A 403 here must not
        // paint the page-level forbidden banner after the report already opened.
      }
    } catch (e: any) {
      setReportError(
        String(e?.response?.data?.error || e?.response?.data?.detail || e?.message || e) ||
          'Safety Scan could not be opened.',
      );
    }
  }, []);

  const _runReportAction = useCallback(async (opts?: {
    forceGenerate?: boolean;
    reportKindOverride?: ReportDialogKind;
    windowOverride?: { fromIso?: string; toIso?: string };
  }) => {
    const effectiveReportKind = opts?.reportKindOverride || reportKind;
    const resolvedWindow = resolveReportWindowIso(reportFrom, reportTo, searchParams);
    const fromIso = opts?.windowOverride?.fromIso || resolvedWindow.fromIso;
    const toIso = opts?.windowOverride?.toIso || resolvedWindow.toIso;
    const siteId = String(reportSiteId || getScopedSiteId()).trim() || 'default';
    const orgId = getScopedOrgId();
    const needsTimeWindow =
      effectiveReportKind === 'incidents_rollup'
      || effectiveReportKind === 'reliability_bundle'
      || effectiveReportKind === 'proof_pack'
      || effectiveReportKind === 'trust_pack';
    if (needsTimeWindow && (!fromIso || !toIso)) {
      setReportError(
        !toIso && fromIso
          ? 'Report end time is missing. Set the end time in Report scope and click Update Report.'
          : !fromIso && toIso
            ? 'Report start time is missing. Set the start time in Report scope and click Update Report.'
            : 'Please provide a valid From/To date range.',
      );
      return;
    }
    const fromIsoReq = fromIso as string;
    const toIsoReq = toIso as string;

    if (effectiveReportKind === 'proof_pack' && hasBuiltProofPackPreview(proofPackPreviewData) && !opts?.forceGenerate) {
      await _downloadProofPackAction();
      return;
    }

    try {
      setReportLoading(true);
      setReportError(null);
      setReportNotice(null);
      if (effectiveReportKind === 'proof_pack' && opts?.forceGenerate) {
        setProofPackPreviewData(null);
      }

      if (effectiveReportKind === 'incidents_rollup') {
        setReliabilityPreviewData(null);
        setProofPackPreviewData(null);
        setCraPreviewData(null);
        setSiteReadinessPreviewData(null);
        const params = {
          from_date: fromIsoReq,
          to_date: toIsoReq,
          org_id: orgId,
          site_id: siteId,
          device_id: reportDeviceId || undefined,
          zone_id: reportZoneId || undefined,
          camera_ids: reportCameraIds || [],
          policy_type: reportPolicyType || undefined,
          policy_ids: reportPolicyIds || [],
        };
        const jobCreate = await reportsApi.createAsyncReportJob({
          report_kind: 'incidents_rollup',
          params: {
            ...params,
            camera_id: reportCameraIds || [],
            policy_id: reportPolicyIds || [],
          } as any,
          use_cache: true,
        });
        let data: any = jobCreate?.result || null;
        const jobId = String(jobCreate?.job?.job_id || '').trim();
        const cacheHit = Boolean((jobCreate as any)?.job?.cache_hit);
        let usedTimeoutFallback = false;

        if (!data && jobId) {
          const started = Date.now();
          const timeoutMs = 45_000;
          while (!data && Date.now() - started < timeoutMs) {
            await new Promise((resolve) => window.setTimeout(resolve, 800));
            const job = await reportsApi.getAsyncReportJob(jobId, { include_result: true });
            const st = String(job?.job?.status || '').toLowerCase();
            if (st === 'completed') {
              data = job?.result || null;
              break;
            }
            if (st === 'failed') {
              throw new Error(String(job?.job?.error || 'Async report job failed'));
            }
          }
          if (!data) {
            try {
              data = await reportsApi.getIncidentsRollup(params);
              usedTimeoutFallback = true;
            } catch (fallbackErr: any) {
              throw new Error(
                String(fallbackErr?.response?.data?.error || fallbackErr?.message || fallbackErr) ||
                  'Report failed (timeout). Please retry or narrow the date range.',
              );
            }
          }
        }

        if (!data) {
          data = await reportsApi.getIncidentsRollup(params);
        }
        setReportData(data);
        if (!data?.ok) {
          setReportError(String((data as any)?.error || 'Report failed'));
          return;
        }
        setReportNotice(
          cacheHit
            ? 'Incident rollup generated (cache hit).'
            : usedTimeoutFallback
              ? 'Incident rollup generated (async timeout fallback).'
              : 'Incident rollup generated.',
        );
        return;
      }

      if (effectiveReportKind === 'reliability_bundle') {
        setReportData(null);
        setProofPackPreviewData(null);
        setCraPreviewData(null);
        setSiteReadinessPreviewData(null);
        if (reportScope !== 'site' && !String(reportScopeId || '').trim()) {
          setReportError(`Please choose a ${reportScope} for the selected scope.`);
          return;
        }
        const preview = await reportsApi.getReliabilityBundlePreview({
          start_ts: fromIsoReq,
          end_ts: toIsoReq,
          org_id: orgId,
          site_id: siteId,
          scope: reportScope,
          scope_id: reportScope === 'site' ? undefined : String(reportScopeId || '').trim(),
        });
        setReliabilityPreviewData(preview);
        if (!preview?.ok) {
          setReportError('Reliability bundle preview generation failed.');
          return;
        }
        if (reportReliabilityFormat === 'json') {
          setReportNotice('Reliability bundle preview generated.');
          return;
        }
        const { blob, filename } = await reportsApi.downloadReliabilityBundle({
          start_ts: fromIsoReq,
          end_ts: toIsoReq,
          org_id: orgId,
          site_id: siteId,
          scope: reportScope,
          scope_id: reportScope === 'site' ? undefined : String(reportScopeId || '').trim(),
          format: reportReliabilityFormat,
        });
        downloadBlob(blob, filename);
        setReportNotice(`Reliability preview generated. Downloaded ${filename}`);
        return;
      }

      if (effectiveReportKind === 'trust_pack') {
        setReportData(null);
        setReliabilityPreviewData(null);
        setProofPackPreviewData(null);
        setCraPreviewData(null);
        setSiteReadinessPreviewData(null);
        const started = await reportsApi.startTrustPackBuild({
          from_date: fromIsoReq,
          to_date: toIsoReq,
          org_id: orgId,
          site_id: siteId,
        });
        const jobId = String(started?.job_id || '').trim();
        if (!started?.ok || !jobId) {
          setReportError(describeTrustPackStartError(started));
          return;
        }
        setTrustPackJobId(jobId);
        setReportNotice(`${REPORT_KIND_LABELS.trust_pack} build started... this can take several minutes.`);
        return;
      }

      if (effectiveReportKind === 'cra_readiness') {
        setReportData(null);
        setReliabilityPreviewData(null);
        setProofPackPreviewData(null);
        setSiteReadinessPreviewData(null);

        const useEdgeModels = Boolean(craUseEdgeModels);
        const edgeMode = (craEdgeMode as any) === 'clip' ? 'clip' : 'live';
        const liveCameraId = String(craLiveCameraId || '').trim();
        const edgeDeviceId = String(craEdgeDeviceId || '').trim();
        const edgeCameraId = String(craEdgeCameraId || '').trim();
        if (useEdgeModels) {
          if (!edgeDeviceId || !edgeCameraId) {
            setReportError('Please choose an edge box and a camera for edge CRA.');
            return;
          }
          if (edgeMode === 'clip' && !craClipFile) {
            setReportError('Please choose a clip file for edge clip CRA.');
            return;
          }
        } else if (!craClipFile && !craPrecomputedFile && !liveCameraId) {
          setReportError('Please choose a clip file, a precomputed frames JSON file, or a live camera.');
          return;
        }

        const parseOptionalNumber = (raw: string, label: string, mode: 'float' | 'int'): number | undefined => {
          const s = String(raw || '').trim();
          if (!s) return undefined;
          const n = Number(s);
          if (!Number.isFinite(n)) {
            throw new Error(`${label} must be a valid number.`);
          }
          if (n <= 0) {
            throw new Error(`${label} must be greater than 0.`);
          }
          return mode === 'int' ? Math.floor(n) : n;
        };

        const acceptanceModeRequested = Boolean(
          craAcceptanceMode || craCaptureProtocolFile || String(craCaptureScenario || '').trim(),
        );
        if (acceptanceModeRequested && !craDeviceMetadataFile) {
          setReportError('Acceptance mode requires a device metadata JSON file.');
          return;
        }
        if (acceptanceModeRequested && !craCaptureProtocolFile) {
          setReportError('Acceptance mode requires a capture protocol JSON file.');
          return;
        }

        const craInput = {
          task: String(craTask || '').trim() || 'ppe_gloves_goggles_workstation@v0',
          camera_view_id:
            String(craCameraViewId || '').trim() || (useEdgeModels ? edgeCameraId : liveCameraId) || undefined,
          source: useEdgeModels ? (edgeMode === 'clip' ? 'edge_clip' : 'edge_precomputed') : undefined,
          edge_device_id: useEdgeModels ? edgeDeviceId : undefined,
          include_edge_frames_zip: useEdgeModels ? Boolean(craIncludeEdgeFramesZip) : undefined,
          camera_id: useEdgeModels ? edgeCameraId : liveCameraId || undefined,
          capture_sec:
            useEdgeModels && edgeMode === 'clip'
              ? undefined
              : parseOptionalNumber(craCaptureSec, useEdgeModels ? 'Capture seconds (edge)' : 'Capture seconds', 'int'),
          clipFile: useEdgeModels ? (edgeMode === 'clip' ? craClipFile || undefined : undefined) : craClipFile || undefined,
          precomputedFile: useEdgeModels ? undefined : craPrecomputedFile || undefined,
          roiFile: craRoiFile || undefined,
          deviceMetadataFile: craDeviceMetadataFile || undefined,
          acceptance_mode: acceptanceModeRequested ? true : craDeviceMetadataFile ? false : undefined,
          captureProtocolFile: craCaptureProtocolFile || undefined,
          capture_scenario: String(craCaptureScenario || '').trim() || undefined,
          annotate_frames: useEdgeModels ? false : Boolean(craAnnotateFrames),
          include_raw_frames_json: Boolean(craIncludeRawFrames),
          analysis_fps: parseOptionalNumber(craAnalysisFps, 'Analysis FPS', 'float'),
          max_duration_sec: parseOptionalNumber(craMaxDurationSec, 'Max duration (sec)', 'int'),
          max_frames: parseOptionalNumber(craMaxFrames, 'Max frames', 'int'),
        };

        if (craFormat === 'json') {
          const res = await reportsApi.runCraReadinessJson(craInput);
          setCraPreviewData(res);
          if (!res?.ok) {
            setReportError(String(res?.error || res?.detail || `${REPORT_KIND_LABELS.cra_readiness} run failed`));
            return;
          }
          setReportNotice(`${REPORT_KIND_LABELS.cra_readiness} generated.`);
          return;
        }

        setCraPreviewData(null);
        const { blob, filename } = await reportsApi.runCraReadinessDownload({
          ...craInput,
          format: craFormat === 'pdf' ? 'pdf' : 'zip',
        });
        downloadBlob(blob, filename);
        setReportNotice(`${REPORT_KIND_LABELS.cra_readiness} generated. Downloaded ${filename}`);
        return;
      }

      if (effectiveReportKind === 'site_readiness') {
        setReportData(null);
        setReliabilityPreviewData(null);
        setProofPackPreviewData(null);
        setCraPreviewData(null);

        if (!siteReadinessSiteProfileFile || !siteReadinessCamerasFile) {
          setReportError('Please choose both site profile and cameras files.');
          return;
        }

        const parseOptionalNumber = (
          raw: string,
          label: string,
          mode: 'float' | 'int',
          minValue?: number,
        ): number | undefined => {
          const s = String(raw || '').trim();
          if (!s) return undefined;
          const n = Number(s);
          if (!Number.isFinite(n)) {
            throw new Error(`${label} must be a valid number.`);
          }
          if (minValue != null && n < minValue) {
            throw new Error(`${label} must be >= ${minValue}.`);
          }
          return mode === 'int' ? Math.floor(n) : n;
        };

        const sraInput = {
          siteProfileFile: siteReadinessSiteProfileFile || undefined,
          camerasFile: siteReadinessCamerasFile || undefined,
          mode: siteReadinessMode,
          task_profile: String(siteReadinessTaskProfile || '').trim() || undefined,
          rtsp_stability_sec: parseOptionalNumber(siteReadinessRtspStabilitySec, 'RTSP stability sec', 'int', 1),
          rtsp_jitter_fail_ms: parseOptionalNumber(siteReadinessRtspJitterFailMs, 'RTSP jitter fail ms', 'float', 0),
          switch_model: String(siteReadinessSwitchModel || '').trim() || undefined,
          poe_total_watts: parseOptionalNumber(siteReadinessPoeTotalWatts, 'PoE total watts', 'float', 0),
          poe_port_class: String(siteReadinessPoePortClass || '').trim() || undefined,
          poe_ports: parseOptionalNumber(siteReadinessPoePorts, 'PoE ports', 'int', 0),
          powered_cameras: parseOptionalNumber(siteReadinessPoweredCameras, 'Powered cameras', 'int', 0),
          cameras_transport: siteReadinessCamerasTransport,
          rf_rssi_db: parseOptionalNumber(siteReadinessRfRssiDb, 'RF RSSI dB', 'float'),
          rf_channel: parseOptionalNumber(siteReadinessRfChannel, 'RF channel', 'int', 0),
          rf_packet_loss_pct: parseOptionalNumber(siteReadinessRfPacketLossPct, 'RF packet loss %', 'float', 0),
          rf_measurement_method: String(siteReadinessRfMeasurementMethod || '').trim() || undefined,
        };

        if (siteReadinessFormat === 'json') {
          const res = await reportsApi.runSiteReadinessJson(sraInput);
          setSiteReadinessPreviewData(res);
          if (!res?.ok) {
            setReportError(String(res?.error || res?.detail || 'Site readiness run failed'));
            return;
          }
          setReportNotice('Site readiness report generated.');
          return;
        }

        setSiteReadinessPreviewData(null);
        const { blob, filename } = await reportsApi.runSiteReadinessDownload({
          ...sraInput,
          format: siteReadinessFormat === 'pdf' ? 'pdf' : 'zip',
        });
        downloadBlob(blob, filename);
        setReportNotice(`Site readiness report generated. Downloaded ${filename}`);
        return;
      }

      setReportData(null);
      setReliabilityPreviewData(null);
      setCraPreviewData(null);
      setSiteReadinessPreviewData(null);
      setProofPackJobId(null);
      setProofPackJobState(null);
      setProofPackJobLongRunning(false);
      setReportNotice('Building Scan Package…');
      const evidencePackParams = buildEvidencePackParams();
      let proofPreview: ReliabilityProofPackPreviewResponse | null = null;
      let proofPackError: any = null;
      for (let attempt = 0; attempt < PROOF_PACK_BUILD_MAX_ATTEMPTS; attempt += 1) {
        try {
          const packResponse = await reportsApi.getEvidencePack(evidencePackParams);
          // If the API returns an async job stub (caller skipped inline poll), attach job polling.
          const asyncJobId = String((packResponse as any)?.job?.job_id || '').trim();
          if (
            (String((packResponse as any)?.mode || '').trim().toLowerCase() === 'async'
              || Boolean(asyncJobId))
            && !(packResponse as any)?.evidence_pack
          ) {
            if (!asyncJobId) {
              setReportError('Scan Package async job did not return a job id.');
              return;
            }
            setProofPackJobId(asyncJobId);
            setProofPackJobState('queued');
            setReportNotice('Building Scan Package…');
            return;
          }
          proofPreview = normalizeEvidencePackPreview(packResponse);
          if (
            proofPreview
            && !proofPreview.ok
            && shouldRetryProofPackStartError(proofPreview as any)
            && attempt < PROOF_PACK_BUILD_MAX_ATTEMPTS - 1
          ) {
            await new Promise((resolve) => window.setTimeout(resolve, PROOF_PACK_BUILD_RETRY_DELAY_MS));
            continue;
          }
          proofPackError = null;
          break;
        } catch (e: any) {
          proofPackError = e;
          if (
            shouldRetryProofPackStartError(null, e)
            && attempt < PROOF_PACK_BUILD_MAX_ATTEMPTS - 1
          ) {
            await new Promise((resolve) => window.setTimeout(resolve, PROOF_PACK_BUILD_RETRY_DELAY_MS));
            continue;
          }
          throw e;
        }
      }
      if (!proofPreview) {
        setReportError(describeProofPackStartError(null, proofPackError));
        return;
      }
      if (proofPreview?.ok) {
        proofPreview = await attachPersistedSafetyReportDraft({
          preview: proofPreview,
          searchParams,
          orgId,
          siteId,
        });
      }
      setProofPackPreviewData(proofPreview);
      if (proofPreview?.ok) {
        writeAcceptanceProofCache(orgId, siteId, proofPreview, reportDeviceId || undefined, proofScopeCacheKey);
      }
      if (!proofPreview?.ok) {
        setReportError(describeProofPackStartError(proofPreview as any));
        return;
      }
      setReportNotice(
        opts?.forceGenerate
          ? 'Evidence package refreshed. Acceptance should now reflect proof availability.'
          : 'Evidence package ready.',
      );
      return;
    } catch (e: any) {
      if (effectiveReportKind === 'trust_pack') {
        setReportError(describeTrustPackStartError(null, e));
        return;
      }
      if (effectiveReportKind === 'proof_pack') {
        setReportError(describeProofPackStartError(null, e));
        return;
      }
      setReportError(String(e?.response?.data?.error || e?.message || e || 'Report failed'));
    } finally {
      setReportLoading(false);
    }
  }, [
    craAcceptanceMode,
    craAnalysisFps,
    craCameraViewId,
    craCaptureProtocolFile,
    craCaptureScenario,
    craCaptureSec,
    craClipFile,
    craDeviceMetadataFile,
    craEdgeCameraId,
    craEdgeDeviceId,
    craEdgeMode,
    craFormat,
    craIncludeEdgeFramesZip,
    craIncludeRawFrames,
    craLiveCameraId,
    craMaxDurationSec,
    craMaxFrames,
    craPrecomputedFile,
    craRoiFile,
    craTask,
    craUseEdgeModels,
    craAnnotateFrames,
    downloadBlob,
    getScopedOrgId,
    getScopedSiteId,
    reportCameraIds,
    reportDeviceId,
    reportFrom,
    reportKind,
    reportPolicyIds,
    reportPolicyType,
    proofPackPreviewData,
    _downloadProofPackAction,
    buildEvidencePackParams,
    proofScopeCacheKey,
    reportReliabilityFormat,
    reportScope,
    reportScopeId,
    reportSiteId,
    reportTo,
    reportZoneId,
    searchParams,
    siteReadinessCamerasFile,
    siteReadinessCamerasTransport,
    siteReadinessFormat,
    siteReadinessMode,
    siteReadinessPoePortClass,
    siteReadinessPoePorts,
    siteReadinessPoeTotalWatts,
    siteReadinessPoweredCameras,
    siteReadinessRfChannel,
    siteReadinessRfMeasurementMethod,
    siteReadinessRfPacketLossPct,
    siteReadinessRfRssiDb,
    siteReadinessRtspJitterFailMs,
    siteReadinessRtspStabilitySec,
    siteReadinessSiteProfileFile,
    siteReadinessSwitchModel,
    siteReadinessTaskProfile,
  ]);

  return {
    reportDialogOpen,
    setReportDialogOpen,
    reportTask,
    setReportTask,
    reportAdvancedArtifactMode,
    setReportAdvancedArtifactMode,
    reportFormMoreOptions,
    setReportFormMoreOptions,
    reportTaskFirstMoreOpen,
    setReportTaskFirstMoreOpen,
    reportKind,
    setReportKind,
    reportFrom,
    setReportFrom,
    reportTo,
    setReportTo,
    reportSiteId,
    setReportSiteId,
    reportDeviceId,
    setReportDeviceId,
    reportZoneId,
    setReportZoneId,
    reportCameraIds,
    setReportCameraIds,
    reportPolicyType,
    setReportPolicyType,
    reportPolicyIds,
    setReportPolicyIds,
    reportScope,
    setReportScope,
    reportScopeId,
    setReportScopeId,
    reportReliabilityFormat,
    setReportReliabilityFormat,
    reportLoading: reportLoading || Boolean(trustPackJobId) || Boolean(proofPackJobId),
    reportError,
    setReportError,
    reportNotice,
    setReportNotice,
    proofPackJobId,
    proofPackJobState,
    proofPackJobLongRunning,
    reportData,
    reliabilityPreviewData,
    proofPackPreviewData,
    craPreviewData,
    siteReadinessPreviewData,
    craFormat,
    setCraFormat,
    craTask,
    setCraTask,
    craCameraViewId,
    setCraCameraViewId,
    craAcceptanceMode,
    setCraAcceptanceMode,
    craCaptureProtocolFile,
    setCraCaptureProtocolFile,
    craCaptureScenario,
    setCraCaptureScenario,
    craUseEdgeModels,
    setCraUseEdgeModels,
    craEdgeDeviceId,
    setCraEdgeDeviceId,
    craEdgeCameraId,
    setCraEdgeCameraId,
    craEdgeMode,
    setCraEdgeMode,
    craIncludeEdgeFramesZip,
    setCraIncludeEdgeFramesZip,
    craLiveCameraId,
    setCraLiveCameraId,
    craCaptureSec,
    setCraCaptureSec,
    craClipFile,
    setCraClipFile,
    craPrecomputedFile,
    setCraPrecomputedFile,
    craRoiFile,
    setCraRoiFile,
    craDeviceMetadataFile,
    setCraDeviceMetadataFile,
    craAnnotateFrames,
    setCraAnnotateFrames,
    craIncludeRawFrames,
    setCraIncludeRawFrames,
    craAnalysisFps,
    setCraAnalysisFps,
    craMaxDurationSec,
    setCraMaxDurationSec,
    craMaxFrames,
    setCraMaxFrames,
    siteReadinessFormat,
    setSiteReadinessFormat,
    siteReadinessMode,
    setSiteReadinessMode,
    siteReadinessTaskProfile,
    setSiteReadinessTaskProfile,
    siteReadinessSiteProfileFile,
    setSiteReadinessSiteProfileFile,
    siteReadinessCamerasFile,
    setSiteReadinessCamerasFile,
    siteReadinessRtspStabilitySec,
    setSiteReadinessRtspStabilitySec,
    siteReadinessRtspJitterFailMs,
    setSiteReadinessRtspJitterFailMs,
    siteReadinessSwitchModel,
    setSiteReadinessSwitchModel,
    siteReadinessPoeTotalWatts,
    setSiteReadinessPoeTotalWatts,
    siteReadinessPoePortClass,
    setSiteReadinessPoePortClass,
    siteReadinessPoePorts,
    setSiteReadinessPoePorts,
    siteReadinessPoweredCameras,
    setSiteReadinessPoweredCameras,
    siteReadinessCamerasTransport,
    setSiteReadinessCamerasTransport,
    siteReadinessRfRssiDb,
    setSiteReadinessRfRssiDb,
    siteReadinessRfChannel,
    setSiteReadinessRfChannel,
    siteReadinessRfPacketLossPct,
    setSiteReadinessRfPacketLossPct,
    siteReadinessRfMeasurementMethod,
    setSiteReadinessRfMeasurementMethod,
    reportSignalView,
    setReportSignalView,
    reportSuppressionView,
    setReportSuppressionView,
    rulePolicyLoading,
    rulePolicySaving,
    rulePolicyError,
    setRulePolicyError,
    rulePolicyVersion,
    rulePolicyFromDb,
    rulePolicyVersions,
    rulePolicyVersionsLoading,
    rulePolicyPreview,
    rulePolicyPreviewLoading,
    ruleTopAreasLimit,
    setRuleTopAreasLimit,
    ruleTopTypesLimit,
    setRuleTopTypesLimit,
    ruleTopActionsLimit,
    setRuleTopActionsLimit,
    rulePpePerHourRed,
    setRulePpePerHourRed,
    ruleDwellTrendAmber,
    setRuleDwellTrendAmber,
    ruleHighCriticalRed,
    setRuleHighCriticalRed,
    ruleNoIncidentsGrey,
    setRuleNoIncidentsGrey,
    ruleMustActTypes,
    setRuleMustActTypes,
    ruleSafetyCriticalTypes,
    setRuleSafetyCriticalTypes,
    ruleOperationalTypes,
    setRuleOperationalTypes,
    reportFieldVisibility,
    taskRequiresAdvancedArtifactMode,
    showTaskFirstMoreOptions,
    _openReportLink,
    _riskFlagSeverity,
    _loadRulePolicy,
    _buildRulePolicyFromUi,
    _saveRulePolicy,
    _activateRulePolicyVersion,
    _deactivateRulePolicy,
    _previewRulePolicyImpact,
    _openReportDialog,
    _downloadCsv,
    _printReport,
    _runReportAction,
    _downloadProofPackAction,
    downloadPersistedSafetyScanPdf,
    downloadPersistedSafetyScanPackage,
    loadPersistedSafetyReportReadOnly,
    artifactPdfStatus,
    artifactZipStatus,
    proofPackDownloading,
    safetyReportTypeMode,
    setSafetyReportTypeMode,
    hazardClassKey,
    setHazardClassKey,
  };
}
